import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DeckAudience } from '../../data/investorAccess';
import { SlideNumberContext } from './slideNumber';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { CoverSlide } from '../../pages/deck/CoverSlide';
import { ProblemSlide } from '../../pages/deck/ProblemSlide';
import { SolutionSlide } from '../../pages/deck/SolutionSlide';
import { ProductExperienceSlide } from '../../pages/deck/ProductExperienceSlide';
import { CallerExperienceSlide } from '../../pages/deck/CallerExperienceSlide';
import { EmergencySlide } from '../../pages/deck/EmergencySlide';
import { AlwaysAvailableSlide } from '../../pages/deck/AlwaysAvailableSlide';
import { PlatformSlide } from '../../pages/deck/PlatformSlide';
import { CompetitiveSlide } from '../../pages/deck/CompetitiveSlide';
import { MarketSlide } from '../../pages/deck/MarketSlide';
import { GoToMarketSlide } from '../../pages/deck/GoToMarketSlide';
import { BusinessModelSlide } from '../../pages/deck/BusinessModelSlide';
import { FinancialOutlookSlide } from '../../pages/deck/FinancialOutlookSlide';
import { PlatformExpansionSlide } from '../../pages/deck/PlatformExpansionSlide';
import { TeamSlide } from '../../pages/deck/TeamSlide';
import { RaiseSlide } from '../../pages/deck/RaiseSlide';
import { InvestmentThesisSlide } from '../../pages/deck/InvestmentThesisSlide';
import { ThankYouSlide } from '../../pages/deck/ThankYouSlide';
interface DeckSlide {
  id: string;
  label: string;
  path: string;
  Component: () => JSX.Element;
}
export const DECK_SLIDES: DeckSlide[] = [{
  id: '01',
  label: 'Cover',
  path: '/deck/1',
  Component: CoverSlide
}, {
  id: '02',
  label: 'The Problem',
  path: '/deck/2',
  Component: ProblemSlide
}, {
  id: '03',
  label: 'The Solution',
  path: '/deck/3',
  Component: SolutionSlide
}, {
  id: '04',
  label: 'Product Experience',
  path: '/deck/4',
  Component: ProductExperienceSlide
}, {
  id: '05',
  label: 'Caller Experience',
  path: '/deck/5',
  Component: CallerExperienceSlide
}, {
  id: '06',
  label: 'Emergency Detection',
  path: '/deck/6',
  Component: EmergencySlide
}, {
  id: '07',
  label: 'Always Available',
  path: '/deck/7',
  Component: AlwaysAvailableSlide
}, {
  id: '08',
  label: 'VEXU Business',
  path: '/deck/8',
  Component: PlatformSlide
}, {
  id: '09',
  label: 'Competitive Positioning',
  path: '/deck/9',
  Component: CompetitiveSlide
}, {
  id: '10',
  label: 'Market Opportunity',
  path: '/deck/10',
  Component: MarketSlide
}, {
  id: '11',
  label: 'Go-to-Market',
  path: '/deck/11',
  Component: GoToMarketSlide
}, {
  id: '12',
  label: 'Business Model',
  path: '/deck/12',
  Component: BusinessModelSlide
}, {
  id: '13',
  label: 'Financial Outlook',
  path: '/deck/13',
  Component: FinancialOutlookSlide
}, {
  id: '14',
  label: 'Platform Expansion',
  path: '/deck/14',
  Component: PlatformExpansionSlide
}, {
  id: '15',
  label: 'Team',
  path: '/deck/15',
  Component: TeamSlide
}, {
  id: '16',
  label: 'The Raise',
  path: '/deck/16',
  Component: RaiseSlide
}, {
  id: '17',
  label: 'Investment Thesis',
  path: '/deck/17',
  Component: InvestmentThesisSlide
}, {
  id: '18',
  label: 'Thank You',
  path: '/deck/18',
  Component: ThankYouSlide
}];

/** Slides that only investors see (the investment ask). */
const INVESTOR_ONLY_SLIDES = ['The Raise'];
interface DeckShellProps {
  initialIndex?: number;
  audience?: DeckAudience;
}

/** Presentation runner: arrow keys, click, or the corner controls move slides. */
export function DeckShell({
  initialIndex = 0,
  audience = 'investor'
}: DeckShellProps) {
  const slides = useMemo(() => audience === 'marketing' ? DECK_SLIDES.filter(s => !INVESTOR_ONLY_SLIDES.includes(s.label)) : DECK_SLIDES, [audience]);
  const [index, setIndex] = useState(() => {
    // Map a full-deck position onto the visible list (skipping hidden slides).
    const target = DECK_SLIDES[initialIndex];
    const pos = slides.findIndex(s => s === target);
    if (pos >= 0) return pos;
    const after = slides.findIndex(s => DECK_SLIDES.indexOf(s) > initialIndex);
    return after >= 0 ? after : slides.length - 1;
  });
  const go = useCallback((next: number) => {
    setIndex(prev => {
      const clamped = Math.min(Math.max(next, 0), slides.length - 1);
      if (clamped !== prev && typeof window !== 'undefined') {
        window.history.replaceState(null, '', slides[clamped].path);
      }
      return clamped;
    });
  }, [slides]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (['ArrowRight', 'PageDown', ' '].includes(e.key)) {
        e.preventDefault();
        setIndex(prev => {
          const next = Math.min(prev + 1, slides.length - 1);
          window.history.replaceState(null, '', slides[next].path);
          return next;
        });
      }
      if (['ArrowLeft', 'PageUp'].includes(e.key)) {
        e.preventDefault();
        setIndex(prev => {
          const next = Math.max(prev - 1, 0);
          window.history.replaceState(null, '', slides[next].path);
          return next;
        });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [slides]);

  // A simulated tap inside an embedded app screen can nudge the document's
  // scroll position; reset it whenever the slide changes so the scaled canvas
  // is never left offset.
  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [index]);
  const {
    Component
  } = slides[index];
  const slideNumber = String(index + 1).padStart(2, '0');
  const {
    rotated,
    width,
    height
  } = useLandscapeLayout();
  const pointerStart = useRef<{
    x: number;
    y: number;
  } | null>(null);
  const triedLock = useRef(false);
  const onPointerDown = (e: React.PointerEvent) => {
    if (e.pointerType === 'mouse') return;
    pointerStart.current = {
      x: e.clientX,
      y: e.clientY
    };
  };
  const onPointerUp = (e: React.PointerEvent) => {
    const start = pointerStart.current;
    pointerStart.current = null;
    if (e.pointerType === 'mouse' || !start) return;
    // Links (e.g. founder contact) and the corner controls keep their own taps.
    if ((e.target as HTMLElement).closest('a, button, input, textarea')) return;
    if (!triedLock.current) {
      triedLock.current = true;
      lockLandscape();
    }

    // Measure along the slide's horizontal axis — physical Y when rotated.
    const along = rotated ? e.clientY : e.clientX;
    const startAlong = rotated ? start.y : start.x;
    const across = rotated ? e.clientX - start.x : e.clientY - start.y;
    const delta = along - startAlong;
    const span = rotated ? window.innerHeight : window.innerWidth;
    if (Math.abs(delta) > 40 && Math.abs(delta) > Math.abs(across)) {
      go(delta < 0 ? index + 1 : index - 1);
      return;
    }
    if (Math.abs(delta) > 12 || Math.abs(across) > 12) return;
    go(along > span / 2 ? index + 1 : index - 1);
  };
  const frameStyle: React.CSSProperties = rotated ? {
    position: 'fixed',
    top: 0,
    left: width,
    width: height,
    height: width,
    transform: 'rotate(90deg)',
    transformOrigin: 'top left'
  } : {};
  return <div className="relative w-full h-full bg-[#EEF2ED] select-none" style={{
    ...frameStyle,
    touchAction: 'manipulation'
  }} onPointerDown={onPointerDown} onPointerUp={onPointerUp} data-id="element-200" data-mp="73s8vbi97uj9">
      {/* Each slide mounts fresh and unmounts immediately on change — an exit
          animation here can strand a slide that owns running timers. */}
      <motion.div key={index} className="absolute inset-0" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 0.4,
      ease: [0.22, 0.61, 0.36, 1]
    }} data-id="element-201" data-mp="2nqurzcj61b9h">
        <SlideNumberContext.Provider value={slideNumber} data-id="element-202" data-mp="vos1ci5mff3j">
          <Component data-id="element-203" data-mp="hs52uay0sgvi" />
        </SlideNumberContext.Provider>
      </motion.div>

      {/* Slide controls */}
      <nav aria-label="Slide navigation" className="deck-type absolute bottom-6 right-6 z-50 flex items-center gap-3 rounded-full bg-white/70 px-3 py-2 backdrop-blur-xl ring-1 ring-[#0B1712]/[0.07] shadow-[0_8px_24px_-12px_rgba(11,23,18,0.25)]" data-id="element-204" data-mp="mqzm1dzwg7g5">
        <button type="button" onClick={() => go(index - 1)} disabled={index === 0} aria-label="Previous slide" className="grid h-7 w-7 place-items-center rounded-full text-[#33463B] transition-colors hover:bg-[#0B1712]/[0.06] disabled:opacity-25 disabled:hover:bg-transparent" data-id="element-205" data-mp="10qheq4gbg4us">
          <ChevronLeft className="h-4 w-4" data-id="element-206" data-mp="1y6wj5phw5xog" />
        </button>
        <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#5A6B60] tabular-nums" data-id="element-207" data-mp="27jn7sauh9qe8">
          {slideNumber} / {String(slides.length).padStart(2, '0')}
        </span>
        <button type="button" onClick={() => go(index + 1)} disabled={index === slides.length - 1} aria-label="Next slide" className="grid h-7 w-7 place-items-center rounded-full text-[#33463B] transition-colors hover:bg-[#0B1712]/[0.06] disabled:opacity-25 disabled:hover:bg-transparent" data-id="element-208" data-mp="2h9own4unncu7">
          <ChevronRight className="h-4 w-4" data-id="element-209" data-mp="1klio46um40an" />
        </button>
      </nav>
    </div>;
}

/**
 * On a touch device held upright, the deck is rotated a quarter turn so it
 * always presents in landscape. Once the device itself is landscape (or the
 * orientation lock succeeds), no rotation is applied.
 */
function useLandscapeLayout() {
  const read = () => {
    if (typeof window === 'undefined') return {
      rotated: false,
      width: 0,
      height: 0
    };
    const touch = window.matchMedia('(pointer: coarse)').matches;
    const w = window.innerWidth;
    const h = window.innerHeight;
    return {
      rotated: touch && h > w,
      width: w,
      height: h
    };
  };
  const [layout, setLayout] = useState(read);
  useEffect(() => {
    const update = () => setLayout(read());
    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
    };
  }, []);
  return layout;
}

/** Best effort: fullscreen + landscape lock where the browser allows it (Android). */
function lockLandscape() {
  const orientation = screen.orientation as ScreenOrientation & {
    lock?: (o: string) => Promise<void>;
  };
  if (!orientation?.lock) return;
  const root = document.documentElement;
  const enter = root.requestFullscreen ? root.requestFullscreen() : Promise.resolve();
  enter.then(() => orientation.lock?.('landscape')).catch(() => undefined);
}